import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AreaProvider } from './contexts/AreaContext';
import { StatusProvider } from './contexts/StatusContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import SiteLayout from './website/SiteLayout';
import M38Layout from './website/miteinander/M38Layout';
import M38Landing from './website/miteinander/Landing';
import M38Danke from './website/miteinander/Danke';
import { activeSiteKey } from './website/sites';
import Home from './website/pages/Home';
import Betreuung24h from './website/pages/Betreuung24h';
import Ortsseite24h from './website/pages/Ortsseite24h';
import HauswirtschaftPage from './website/pages/Hauswirtschaft';
import Kosten from './website/pages/Kosten';
import UeberUns from './website/pages/UeberUns';
import Karriere from './website/pages/Karriere';
import Kontakt from './website/pages/Kontakt';
import Aktuelles from './website/pages/Aktuelles';
import NewsArtikel from './website/pages/NewsArtikel';
import Impressum from './website/pages/Impressum';
import Datenschutz from './website/pages/Datenschutz';
import NotFound from './website/pages/NotFound';
import './App.css';

// Die interne Anwendung wird erst geladen, wenn jemand sie aufruft. Die
// öffentliche Website teilt sich das Bündel mit ihr — und Google rechnet bei der
// Ladezeit alles ein, was vor dem größten Inhalt angefordert wird. Mit allen
// internen Seiten im Hauptbündel waren das 410 KB JavaScript für eine Seite,
// die davon nichts braucht; PageSpeed schätzte das LCP mobil auf 4,5–6 s.
// Die Website-Seiten bleiben bewusst statisch importiert: Sie werden
// vorgerendert und hydriert, und Hydrieren darf nicht auf Nachladen warten.
//
// Jede nachgeladene Seite bringt ihre eigene Suspense-Grenze mit. Eine Grenze um
// alle Routen wäre einfacher — aber Suspense im Baum der vorgerenderten Seiten
// lässt das Hydrieren scheitern, weil das HTML die dazugehörigen Marker nicht hat.
const lazyPage = (loader: () => Promise<{ default: React.ComponentType<any> }>): React.FC => {
  const Page = React.lazy(loader);
  return () => (
    <React.Suspense fallback={<div className="loading"><div className="spinner" /></div>}>
      <Page />
    </React.Suspense>
  );
};
const Login = lazyPage(() => import('./pages/Login'));
const SystemSelect = lazyPage(() => import('./pages/SystemSelect'));
const Dashboard = lazyPage(() => import('./pages/Dashboard'));
const Employees = lazyPage(() => import('./pages/Employees'));
const EmployeeDetail = lazyPage(() => import('./pages/EmployeeDetail'));
const Customers = lazyPage(() => import('./pages/Customers'));
const CustomerDetail = lazyPage(() => import('./pages/CustomerDetail'));
const Carers = lazyPage(() => import('./pages/Carers'));
const CarerDetail = lazyPage(() => import('./pages/CarerDetail'));
const Families = lazyPage(() => import('./pages/Families'));
const FamilyDetail = lazyPage(() => import('./pages/FamilyDetail'));
const Prospects = lazyPage(() => import('./pages/Prospects'));
const Tagespflegen = lazyPage(() => import('./pages/Tagespflegen'));
const MapView = lazyPage(() => import('./pages/MapView'));
const Calendar = lazyPage(() => import('./pages/Calendar'));
const Vertretungen = lazyPage(() => import('./pages/Vertretungen'));
const ImportExport = lazyPage(() => import('./pages/ImportExport'));
const ContactRequests = lazyPage(() => import('./pages/ContactRequests'));
const Users = lazyPage(() => import('./pages/Users'));
const Settings = lazyPage(() => import('./pages/Settings'));
const WebsiteBeitraege = lazyPage(() => import('./pages/WebsiteBeitraege'));
const Auswertung = lazyPage(() => import('./pages/Auswertung'));
const Handbuch = lazyPage(() => import('./pages/Handbuch'));

const App: React.FC = () => {
  const version = "v1.0.7-manual-coordinates";
  console.log(`OptiVisit Frontend ${version} - Manual Coordinate Editing`);

  // Welcher öffentliche Auftritt ausgeliefert wird, hängt am Host der Anfrage
  // (siehe `website/sites.ts`). Einmal beim Start ermittelt: Der Host wechselt
  // während einer Sitzung nicht, und ein Zustand wäre nur eine weitere Stelle,
  // an der etwas nicht zusammenpassen kann.
  const site = activeSiteKey();

  return (
    <Router>
      <AuthProvider>
        <AreaProvider>
        <StatusProvider>
        <div className="App">
          <Toaster position="top-right" />
          <Routes>
            {/*
              Öffentliche Website — liegt vor der App und braucht kein Login.

              Unter der Kampagnendomain steht hier ein anderer Baum: die
              Landingpage ohne Navigation, dazu Danke-Seite und die beiden
              Rechtstexte. Die Seiten der Kundenwebsite sind dort bewusst nicht
              erreichbar — das Konzept will genau einen Weg, und nginx liefert
              für sie unter diesem Host ohnehin kein vorgerendertes HTML aus.

              Die Rechtstexte sind dieselben Komponenten: Impressum und
              Datenschutzerklärung eines Betriebs gibt es nur einmal.
            */}
            {site === 'miteinander38' ? (
              <Route element={<M38Layout />}>
                <Route path="/" element={<M38Landing />} />
                <Route path="/danke" element={<M38Danke />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            ) : (
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/24h-betreuung" element={<Betreuung24h />} />
                <Route path="/24h-betreuung/:ort" element={<Ortsseite24h />} />
                <Route path="/hauswirtschaft" element={<HauswirtschaftPage />} />
                <Route path="/kosten" element={<Kosten />} />
                <Route path="/ueber-uns" element={<UeberUns />} />
                <Route path="/karriere" element={<Karriere />} />
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/aktuelles" element={<Aktuelles />} />
                <Route path="/aktuelles/:slug" element={<NewsArtikel />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            )}

            {/* Interne Anwendung */}
            <Route path="/login" element={<Login />} />
            <Route
              path="/select-system"
              element={
                <ProtectedRoute>
                  <SystemSelect />
                </ProtectedRoute>
              }
            />
            {/* Handbuch: für jede Rolle, Teil über den Pfad (/handbuch/hauswirtschaft …). */}
            {['/handbuch', '/handbuch/:teil'].map(path => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Handbuch />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/carers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Carers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/carers/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CarerDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/families"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Families />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/families/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FamilyDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/prospects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Prospects />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tagespflegen"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Tagespflegen />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/map"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MapView />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Calendar />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vertretungen"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Vertretungen />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/anfragen"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContactRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/import"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ImportExport />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/website-beitraege"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <WebsiteBeitraege />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auswertung"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Auswertung />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
        </StatusProvider>
        </AreaProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;